import React, { useEffect, useState } from "react";
import { Mic } from "lucide-react";
import { api } from "@/lib/api";

const money = (v, d = 2) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const WEEKS = [[4, "4 wks"], [8, "8 wks"], [13, "13 wks"], [26, "26 wks"]];

function Stat({ label, value, sub, testId }) {
  return (
    <div className="bg-ivory border border-champagne/70 rounded-lg p-3" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-widest text-cocoa/50">{label}</p>
      <p className="mt-0.5 font-heading text-2xl text-cocoa">{value}</p>
      {sub && <p className="text-[11px] text-cocoa/55">{sub}</p>}
    </div>
  );
}

function WeekBars({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.notes));
  return (
    <div className="flex items-end gap-1.5 h-24" data-testid="voice-weekly-bars">
      {rows.map((r, i) => (
        <div key={r.week} className="flex-1 min-w-0 flex flex-col items-center justify-end gap-1" title={`w/c ${r.week}: ${r.notes} notes · ${r.minutes} min · ${money(r.usd, 3)}${r.failed ? ` · ${r.failed} failed` : ""}`}>
          <div className="w-full rounded-t bg-gold" style={{ height: `${Math.max(3, Math.round((72 * r.notes) / max))}px` }} />
          <span className={`text-[9px] text-cocoa/45 whitespace-nowrap ${rows.length > 6 && i % 2 ? "invisible sm:visible" : ""}`}>{r.week.slice(5).replace("-", "/")}</span>
        </div>
      ))}
    </div>
  );
}

export function AdminVoiceInsights() {
  const [weeks, setWeeks] = useState(8);
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/analytics/voice?weeks=${weeks}`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [weeks]);
  const t = d?.totals; const u = d?.upsell;

  return (
    <div className="ss-card p-5" data-testid="admin-voice-insights">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[10px] uppercase tracking-widest text-cocoa/50 inline-flex items-center gap-1.5"><Mic size={11} className="text-gold" /> Voice notes · Luxe</p>
        <div className="flex flex-wrap gap-1">{WEEKS.map(([k, l]) => <button key={k} onClick={() => setWeeks(k)} data-testid={`voice-weeks-${k}`} className={`ss-pill h-8 px-3 text-xs whitespace-nowrap ${weeks === k ? "ss-pill-primary" : "ss-pill-ghost"}`}>{l}</button>)}</div>
      </div>
      {d?.error && <p className="mt-3 text-sm text-rose">Couldn't load voice insights.</p>}
      {t && (
        <>
          <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-2">
            <Stat label="Notes transcribed" value={t.notes.toLocaleString()} sub={`${t.mic} mic · ${t.upload} uploaded${t.failed ? ` · ${t.failed} failed` : ""}`} testId="voice-stat-notes" />
            <Stat label="Audio minutes" value={t.minutes.toLocaleString()} sub={`avg ${t.avg_seconds}s per note`} testId="voice-stat-minutes" />
            <Stat label="Whisper cost" value={money(t.usd, 3)} sub={`≈ ${money(t.avg_usd_per_note, 4)} per note`} testId="voice-stat-cost" />
            <Stat label="Luxe adoption" value={t.adoption_pct == null ? "—" : `${t.adoption_pct}%`} sub={`${t.adopters} of ${t.luxe_members} Luxe members`} testId="voice-stat-adoption" />
          </div>
          <div className="mt-4 grid lg:grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-cocoa/50 mb-2">Notes per week</p>
              <WeekBars rows={d.series} />
            </div>
            <div data-testid="voice-upsell-funnel">
              <p className="text-[10px] uppercase tracking-widest text-cocoa/50 mb-2">Upsell funnel · non-Luxe members</p>
              {[["Saw the locked control", u.unique_viewers, u.views], ["Tapped “See Luxe”", u.unique_clickers, u.cta_clicks], ["Now on Luxe", u.converted_to_luxe, null]].map(([l, n, raw], i, arr) => (
                <div key={l} className="mb-2">
                  <div className="flex justify-between text-sm text-cocoa"><span>{l}</span><span className="font-medium">{n}{raw != null && raw !== n && <span className="text-xs text-cocoa/50 ml-1">({raw} views)</span>}</span></div>
                  <div className="mt-1 h-2 rounded-full bg-champagne/70 overflow-hidden"><div className="h-full bg-gold/80" style={{ width: `${Math.max(1, (100 * n) / Math.max(1, arr[0][1]))}%` }} /></div>
                </div>
              ))}
              <p className="text-xs text-cocoa/55">Conversion {u.conversion_pct == null ? "—" : `${u.conversion_pct}%`} · surfaces: {Object.entries(u.by_surface || {}).map(([k, v]) => `${k} ${v}`).join(" · ") || "none yet"}</p>
            </div>
          </div>
          {d.top_members?.length > 0 && (
            <table className="mt-4 w-full text-sm ss-table-stack" data-testid="voice-top-members">
              <thead className="text-[10px] uppercase tracking-widest text-cocoa/50"><tr><th className="text-left py-1">Member</th><th className="text-right py-1">Notes</th><th className="text-right py-1">Min</th><th className="text-right py-1">Cost</th><th className="text-right py-1">Last</th></tr></thead>
              <tbody>{d.top_members.map((m) => (
                <tr key={m.user_id} className="border-t border-champagne/60 text-cocoa"><td className="py-1.5 sm:truncate sm:max-w-[220px] break-all font-medium sm:font-normal">{m.email}<span className="text-xs text-cocoa/45 ml-1">({m.plan})</span></td><td data-label="Notes" className="text-right">{m.notes}</td><td data-label="Minutes" className="text-right">{m.minutes}</td><td data-label="Cost" className="text-right">{money(m.usd, 3)}</td><td data-label="Last" className="text-right text-xs text-cocoa/55">{(m.last_at || "").slice(0, 10)}</td></tr>
              ))}</tbody>
            </table>
          )}
          <p className="mt-3 text-xs text-cocoa/50">{d.note}</p>
        </>
      )}
    </div>
  );
}
